import { EditFilled, CheckCircleOutlined, ExclamationCircleTwoTone } from "@ant-design/icons";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Checkbox, Collapse, Descriptions, Tooltip } from "antd";
import React, { FC } from "react";
import { Link } from "react-router-dom";
import { VPDLabel } from "~/context/taxonomy-context";
import {
  VpdList,
  isSinglePrice,
  GpmType,
  isRangeGpm,
  SortableItem,
  DraggableHandle,
} from "~/design-system";
import { Gpm } from "~/design-system/components/matter-editor/gpm";
import { createFromRangeOrSingle } from "~/design-system/components/matter-editor/pricing-text-creators";
import { ParentMatterPhasesPartsFragment } from "~/gql/graphql";
import { useToggleIncludeProposal } from "./hooks/update-parent-matter";
import { useParentOrder } from "./order-parents-hook";

type Props = {
  phase: ParentMatterPhasesPartsFragment;
  pmsName: string;
  parentMatterId: number;
  excludeTax: boolean;
  taxLevelPercent: number;
  administrationCharge: number;
};

export const ParentMatterPhaseItem: FC<Props> = ({
  phase,
  pmsName,
  parentMatterId,
  excludeTax,
  taxLevelPercent,
  administrationCharge,
}) => {
  const PA_LABEL = VPDLabel("PracticeArea");
  const FE_LABEL = VPDLabel("FeeEarner");

  const toggleIncludeInProposal = useToggleIncludeProposal();

  const { onReOrder, orderedItems, sensors } = useParentOrder(
    phase.id,
    parentMatterId,
    phase.matters ?? []
  );

  return (
    <Collapse key={phase.id}>
      <Collapse.Panel
        showArrow
        header={phase.title}
        key={phase.id}
        extra={
          <Checkbox
            onChange={(e) => toggleIncludeInProposal(e.target.checked, phase.id)}
            checked={phase.includeInProposalOutput ?? false}
          >
            Include in proposal
          </Checkbox>
        }
      >
        <DndContext
          key={phase.id}
          onDragEnd={({ active, over }) =>
            over && onReOrder(parseInt(active.id), parseInt(over.id))
          }
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            key={phase.id}
            items={phase.matters.map((x) => x.id.toString())}
            strategy={verticalListSortingStrategy}
          >
            <VpdList
              itemTextProperty={"title"}
              itemKeyProperty="id"
              split
              labels={{ emptyText: `No Proposals for ${phase.title}` }}
              listStyle="primary"
              items={orderedItems}
              renderItem={(matter) => {
                const totals = createFromRangeOrSingle({
                  pricingSummary: matter.priceOptions!,
                  phases: matter.phases,
                  disbursements: matter.disbursements,
                  uplifts: matter.uplifts,
                  discounts: matter.discounts,
                  technologyCharges: matter.technologyCharges,
                  matterWork: matter.work,
                  pricingLevel: matter.pricingLevel,
                  pricingType: matter.pricingType,
                  excludeTax,
                  taxLevelPercent,
                  administrationCharge,
                  waiveAdminCharge: matter.waiveAdminCharge ?? false,
                  currencySymbol: "£",
                });
                const gpm = isSinglePrice(totals?.gpm)
                  ? { fixed: totals?.gpm, type: GpmType.Fixed }
                  : {
                      lower: totals.gpm?.lower,
                      upper: totals.gpm?.upper,
                      type: GpmType.Range,
                    };

                const showGpm = isRangeGpm(gpm)
                  ? gpm?.lower !== undefined && gpm?.upper !== undefined
                  : gpm?.fixed !== undefined;

                return (
                  <SortableItem id={matter.id.toString()} key={matter.id}>
                    {({ attributes, listeners }) => (
                      <VpdList.Item
                        contentSuffix={
                          <Link to={`/matter-editor/${matter.id}`}>
                            <EditFilled style={{ fontSize: 17, marginRight: 5 }} />
                          </Link>
                        }
                        contentPrefix={<DraggableHandle {...attributes} {...listeners} />}
                        key={matter.id}
                        content={
                          <div style={{ padding: 15 }}>
                            <Descriptions title={matter.title}>
                              <Descriptions.Item label={PA_LABEL}>
                                {matter.organisationalUnit?.name}
                              </Descriptions.Item>
                              <Descriptions.Item label={`Lead ${FE_LABEL}`}>
                                {matter.leadFeeEarner?.name ?? "Not set"}
                              </Descriptions.Item>
                              <Descriptions.Item label={`Linked to ${pmsName} matter?`}>
                                {matter.externalMatterId ? (
                                  <span>
                                    <CheckCircleOutlined
                                      style={{
                                        color: "green",
                                        fontSize: "1.5rem",
                                        marginRight: 3,
                                      }}
                                    />
                                    {matter.externalMatter?.externalReference &&
                                      `(${matter.externalMatter?.externalReference})`}
                                  </span>
                                ) : (
                                  <Tooltip title={`Click here to link to ${pmsName}`}>
                                    <Link to={`/matter-editor/${matter.id}/clientmatterdetails`}>
                                      <ExclamationCircleTwoTone
                                        twoToneColor={"#ff4d4f"}
                                        style={{ fontSize: "1.5rem" }}
                                      />
                                    </Link>
                                  </Tooltip>
                                )}
                              </Descriptions.Item>
                              <Descriptions.Item label={"GPM"}>
                                {showGpm ? (
                                  <span>
                                    <Gpm
                                      gpmThreshold={matter.gpmDangerThreshold}
                                      gpm={gpm}
                                      isFeeEarnerLevelGpm={
                                        matter.phases
                                          ?.flatMap((x) => x.work)
                                          ?.some((w) => !w?.feeEarner) ?? false
                                      }
                                    />
                                  </span>
                                ) : (
                                  <span>N/A</span>
                                )}
                              </Descriptions.Item>
                            </Descriptions>
                          </div>
                        }
                      ></VpdList.Item>
                    )}
                  </SortableItem>
                );
              }}
            ></VpdList>
          </SortableContext>
        </DndContext>
      </Collapse.Panel>
    </Collapse>
  );
};
